import React from 'react';
import { Shot as BaseShot, Num, Note, Toc } from './parts';

// Инструкция по созданию событий — испанский текст
const lang = 'es';
const Shot = (props) => <BaseShot lang={lang} {...props} />;

const GuideContent = () => {
  return (
    <div className="guide-content">
      <p className="guide-lead">
        Un evento se crea de una sola vez con el formulario «Crear un evento». Primero elige el tipo de evento
        y la forma en que entrarán las carreras: de eso depende qué campos muestra el formulario. Abajo tienes
        cada paso y cada variante con capturas de pantalla.
      </p>

      <Toc
        title="Contenido"
        items={[
          ['access', 'Antes de empezar'],
          ['choose', 'Elegir la variante del evento'],
          ['basics', 'Paso 1. Lo básico: nombre, descripción, coorganizadores'],
          ['type', 'Paso 2. Tipo de evento'],
          ['status', 'Paso 3. Estado e imagen'],
          ['registration', 'Paso 4. Inscripción y visibilidad'],
          ['scoring', 'Paso 5. Clasificación y puntos'],
          ['wildcards', 'Paso 6. Wild cards (carrera y torneo)'],
          ['sync', 'Variante A. Carreras por plantilla de búsqueda'],
          ['manual', 'Variante B. Carreras de otros usuarios de ROUVY'],
          ['open', 'Variante C. Torneo abierto'],
          ['official', 'Variante D. Evento oficial'],
          ['after', 'Después de crear: edición y coorganizadores'],
          ['problems', 'Errores frecuentes']
        ]}
      />

      <section id="access">
        <h2>Antes de empezar</h2>
        <ul>
          <li>
            El botón <strong>«Crear un evento»</strong> <Num>1</Num> de la página «Eventos» solo lo ven las cuentas
            que pueden crear eventos. Si no lo ves, contacta con un administrador.
          </li>
          <li>El botón <strong>«Tipos de evento»</strong> <Num>2</Num> abre una guía breve de los tipos.</li>
          <li>
            El tipo <strong>«Torneo abierto»</strong> solo está disponible para cuentas con acceso especial. Las demás no
            lo ven en la lista de tipos.
          </li>
          <li>Para que las carreras se encuentren por plantilla de búsqueda, el autor del evento o un coorganizador debe tener ROUVY conectado.</li>
        </ul>
        <Shot
          name="events-list"
          alt="Página «Eventos»: botones «Tipos de evento» y «Crear un evento»"
          caption="La página «Eventos». El icono del lápiz en la fila del evento (3) abre la edición; lo ven el autor del evento y los administradores."
        />
      </section>

      <section id="choose">
        <h2>Elegir la variante del evento</h2>
        <p>La variante sale de dos decisiones: el <strong>tipo</strong> y <strong>cómo se añaden las carreras</strong>.</p>

        <h3>Tipos de evento</h3>
        <div className="guide-table-wrap">
          <table className="guide-table">
            <thead>
              <tr>
                <th>Tipo</th>
                <th>Recorridos</th>
                <th>Horario</th>
                <th>Límite de carreras por etapa</th>
                <th>Wild cards</th>
                <th>Para qué</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td><strong>Salida en grupo</strong></td>
                <td>1</td>
                <td>Sí</td>
                <td>—</td>
                <td>—</td>
                <td>Una salida tranquila o un entrenamiento con amigos</td>
              </tr>
              <tr>
                <td><strong>Carrera</strong></td>
                <td>1</td>
                <td>Sí</td>
                <td>Sí</td>
                <td>Sí</td>
                <td>Una sola carrera, quizá con varias salidas</td>
              </tr>
              <tr>
                <td><strong>Torneo</strong></td>
                <td>1 o más</td>
                <td>Sí</td>
                <td>Sí</td>
                <td>Sí</td>
                <td>Una serie de etapas en distintos recorridos</td>
              </tr>
              <tr>
                <td><strong>Torneo abierto</strong></td>
                <td>1 o más</td>
                <td>No, solo un periodo</td>
                <td>—</td>
                <td>—</td>
                <td>Cada participante hace los recorridos cuando quiere; cuenta el mejor tiempo</td>
              </tr>
            </tbody>
          </table>
        </div>
        <p className="guide-small">«Recorridos»: cuántos recorridos distintos de ROUVY puede tener el evento. Un recorrido puede tener varias salidas.</p>

        <h3>Formas de añadir carreras</h3>
        <div className="guide-table-wrap">
          <table className="guide-table">
            <thead>
              <tr>
                <th>Forma</th>
                <th>Quién crea las carreras en ROUVY</th>
                <th>Tipos de evento</th>
                <th>Cómo activarla</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td><a href="#sync"><strong>A. Plantilla de búsqueda</strong></a></td>
                <td>El autor del evento o los coorganizadores</td>
                <td>Salida en grupo, carrera, torneo</td>
                <td>Por defecto</td>
              </tr>
              <tr>
                <td><a href="#manual"><strong>B. A mano</strong></a></td>
                <td>Cualquier usuario de ROUVY</td>
                <td>Salida en grupo, carrera, torneo</td>
                <td>Casilla «Carreras creadas por otros usuarios de ROUVY»</td>
              </tr>
              <tr>
                <td><a href="#open"><strong>C. Recorridos sin horario</strong></a></td>
                <td>Cada participante para sí</td>
                <td>Torneo abierto</td>
                <td>Elegir el tipo «Torneo abierto»</td>
              </tr>
              <tr>
                <td><a href="#official"><strong>D. Evento oficial</strong></a></td>
                <td>ROUVY</td>
                <td>Todos menos el torneo abierto</td>
                <td>Estado «Oficial»</td>
              </tr>
            </tbody>
          </table>
        </div>
        <Note kind="warning">
          La forma de añadir carreras se elige al crear el evento y después no se puede cambiar. Si te equivocas,
          crea el evento de nuevo.
        </Note>
      </section>

      <section id="basics">
        <h2>Paso 1. Lo básico: nombre, descripción, coorganizadores</h2>
        <Shot name="create-basics" alt="Parte superior del formulario de creación de evento" caption="Parte superior del formulario para la variante con plantilla de búsqueda" />
        <ol>
          <li><Num>1</Num> <strong>Nombre del evento</strong>: obligatorio. Sin plantilla de búsqueda, las carreras de ROUVY se buscan por este nombre.</li>
          <li>
            <Num>2</Num> <strong>Carreras creadas por otros usuarios de ROUVY</strong>: actívala solo para la <a href="#manual">variante B</a>.
            Entonces desaparece el campo de la plantilla y aparece una lista de carreras.
          </li>
          <li>
            <Num>3</Num> <strong>Plantilla de búsqueda en Rouvy</strong>: la parte del nombre que comparten todas tus carreras de ROUVY de este evento,
            por ejemplo <code>Autumn Cup</code>. Consulta la <a href="#sync">variante A</a>.
          </li>
          <li><Num>4</Num> <strong>Coorganizadores</strong>: usuarios que también pueden añadir carreras. Sus carreras de ROUVY también se encuentran por la plantilla.</li>
          <li><Num>5</Num> <strong>Descripción</strong>: reglas, formato, enlaces. Los participantes la ven en la página del evento.</li>
        </ol>

        <h3>Añadir un coorganizador</h3>
        <p>Empieza a escribir un nombre o apodo (2 caracteres o más) y elige a la persona en la lista. Los elegidos aparecen como etiquetas; la cruz los quita.</p>
        <Shot name="co-organizers" alt="Búsqueda de coorganizadores" caption="Un coorganizador ya está añadido y se elige un segundo de la lista" />
      </section>

      <section id="type">
        <h2>Paso 2. Tipo de evento</h2>
        <p>Debajo de la lista de tipos el formulario muestra una pista sobre el tipo elegido. El tipo decide qué campos vienen después.</p>
        <div className="guide-shot-grid">
          <Shot name="type-group_ride" alt="Tipo Salida en grupo" caption="Salida en grupo: un recorrido, sin límite de carreras ni wild cards" />
          <Shot name="type-race" alt="Tipo Carrera" caption="Carrera: un recorrido, añade carreras por etapa y wild cards" />
          <Shot name="type-tournament" alt="Tipo Torneo" caption="Torneo: varios recorridos, los mismos campos extra que la carrera" />
          <Shot name="type-open_tournament" alt="Tipo Torneo abierto" caption="Torneo abierto: añade el periodo del torneo" />
        </div>
      </section>

      <section id="status">
        <h2>Paso 3. Estado e imagen</h2>
        <Shot name="status-image" alt="Estado e imagen del evento" />
        <ol>
          <li>
            <Num>1</Num> <strong>Estado</strong>: «Propio» sirve casi siempre. «Oficial» es para eventos oficiales de ROUVY,
            consulta la <a href="#official">variante D</a>.
          </li>
          <li>
            <Num>2</Num> <strong>Imagen</strong>: la portada del evento. Usa «Subir una imagen», hasta 5 MB.
            «Quitar la imagen» borra la imagen elegida.
          </li>
        </ol>
      </section>

      <section id="registration">
        <h2>Paso 4. Inscripción y visibilidad</h2>
        <Shot name="registration" alt="Ajustes de inscripción" caption="Un torneo: «Carreras por etapa» solo existe en carreras y torneos" />
        <ol>
          <li>
            <Num>1</Num> <strong>Cierre de inscripción (horas antes de la salida)</strong>: cuántas horas antes de la primera carrera
            se cierra la inscripción. Vacío: se puede inscribir incluso después de la salida.
          </li>
          <li>
            <Num>2</Num> <strong>Carreras por etapa</strong> (carrera y torneo): cuántas carreras de una etapa puede correr un participante para la clasificación.
            El participante se inscribe en carreras concretas y solo esas cuentan. Vacío: cuentan todas.
          </li>
          <li><Num>3</Num> <strong>Evento largo</strong>: la inscripción sigue abierta todo el tiempo, incluso después de todas las carreras.</li>
          <li>
            <Num>4</Num> <strong>Evento oculto</strong>: en la lista de eventos solo lo ven los administradores y los organizadores.
            Los resultados se calculan como siempre y el enlace directo funciona para todos.
          </li>
        </ol>
      </section>

      <section id="scoring">
        <h2>Paso 5. Clasificación y puntos</h2>
        <Shot name="scoring" alt="Clasificación y puntos" caption="La competición por equipos está activa: han aparecido los campos de equipo" />
        <ol>
          <li><Num>1</Num> <strong>Competición individual</strong> y/o <strong>Competición por equipos</strong>. Pueden estar activas las dos.</li>
          <li><Num>2</Num> <strong>Puntos por el primer puesto</strong>: lo que recibe el ganador de la etapa (1000 por defecto).</li>
          <li><Num>3</Num> <strong>Paso de puntuación</strong>: cuánto menos recibe cada puesto siguiente (10 por defecto).</li>
          <li><Num>4</Num> <strong>Integrantes del equipo que puntúan</strong> (solo por equipos): cuántos mejores resultados del equipo cuentan.</li>
          <li>
            <Num>5</Num> <strong>Máximo de integrantes por equipo</strong> (solo por equipos): cuántos ciclistas de un equipo pueden inscribirse.
            Vacío: sin límite.
          </li>
          <li><Num>6</Num> <strong>Crear</strong>: guarda el evento y vuelve a la lista de eventos.</li>
        </ol>
        <Note>
          Puntos por un puesto: <code>puntos por el primer puesto − (puesto − 1) × paso</code>, pero nunca menos de 0.
          Con 1000 y 10: 1.º — 1000, 2.º — 990, 10.º — 910, del 101.º en adelante — 0.
        </Note>
      </section>

      <section id="wildcards">
        <h2>Paso 6. Wild cards (carrera y torneo)</h2>
        <p>Una wild card permite correr una etapa fuera del horario. El bloque solo existe en eventos propios de tipo Carrera y Torneo.</p>
        <ol>
          <li><strong>Wild cards (máximo)</strong>: cuántas wild cards puede usar un participante en el evento. Vacío: sin wild cards.</li>
          <li>
            <strong>Quién crea la wild card</strong> (aparece con más de 0 wild cards):
            <ul>
              <li><strong>La crea el organizador</strong>: el participante indica fecha y hora, el organizador crea una carrera en ROUVY para ello y aprueba la solicitud.</li>
              <li><strong>La crea el participante</strong>: el participante crea una carrera en ROUVY según tus requisitos y envía el enlace; el organizador la aprueba o la rechaza.</li>
            </ul>
          </li>
          <li>
            <strong>Campo de requisitos</strong> (solo «La crea el participante»): lo que debe cumplir la carrera del participante: recorrido, ajustes, hora.
            Los participantes ven este texto al enviar una solicitud.
          </li>
        </ol>
        <div className="guide-shot-grid">
          <Shot name="wildcards-organizer" alt="Wild cards creadas por el organizador" caption="La crea el organizador" />
          <Shot name="wildcards-participant" alt="Wild cards creadas por el participante" caption="La crea el participante: aparece el campo de requisitos" />
        </div>
        <p>El organizador recibe una notificación por cada solicitud.</p>
      </section>

      <section id="sync">
        <h2>Variante A. Carreras por plantilla de búsqueda</h2>
        <p>Es la opción por defecto. Tú (o los coorganizadores) creáis las carreras en ROUVY, y el sistema las encuentra por la plantilla y las añade al evento.</p>
        <ol className="guide-steps">
          <li>Crea las carreras en ROUVY con una parte común en el nombre, por ejemplo <code>Autumn Cup #1</code>, <code>Autumn Cup #2</code>.</li>
          <li>Deja la casilla «Carreras creadas por otros usuarios de ROUVY» <strong>desactivada</strong>.</li>
          <li>Escribe la parte común en <strong>«Plantilla de búsqueda en Rouvy»</strong>: <code>Autumn Cup</code>. Vacío: se usa el nombre del evento.</li>
          <li>Añade coorganizadores si otras personas también crean carreras.</li>
          <li>Completa el resto de pasos y pulsa <strong>«Crear»</strong>.</li>
          <li>La búsqueda de carreras empieza en segundo plano justo después de crear el evento. Las carreras aparecen en unos minutos: compruébalas en la edición del evento.</li>
        </ol>
        <p>En la edición del evento aparecen el bloque de sincronización y la lista de carreras encontradas:</p>
        <Shot name="edit-sync" alt="Sincronización y carreras añadidas" />
        <ol>
          <li><Num>1</Num> <strong>Sincronizar los recorridos</strong>: buscar de nuevo, por ejemplo tras crear carreras nuevas en ROUVY. Puedes cerrar la página.</li>
          <li><Num>2</Num> <strong>Actualizar la lista</strong>: cargar el resultado de la sincronización sin recargar la página.</li>
          <li><Num>3</Num> <strong>Eliminar</strong>: quitar del evento una carrera que sobra.</li>
        </ol>
        <Note>Si cambias el nombre del evento o la plantilla y guardas, la búsqueda se inicia automáticamente.</Note>
      </section>

      <section id="manual">
        <h2>Variante B. Carreras de otros usuarios de ROUVY</h2>
        <p>Si las carreras de ROUVY no las creaste tú ni los coorganizadores, la búsqueda por plantilla no las encontrará. En ese caso se añaden a mano.</p>
        <ol className="guide-steps">
          <li>Activa <strong>«Carreras creadas por otros usuarios de ROUVY»</strong>. El campo de la plantilla desaparece y aparece el bloque «Carreras del evento».</li>
          <li>
            Para cada carrera rellena:
            <ul>
              <li><strong>Recorrido de ROUVY*</strong>: ID del recorrido o enlace como <code>https://riders.rouvy.com/route/12345</code>;</li>
              <li><strong>Nombre del recorrido</strong>: opcional, si quieres uno propio;</li>
              <li><strong>Enlace a la carrera de ROUVY*</strong>: <code>https://riders.rouvy.com/events/…</code>;</li>
              <li><strong>Nombre de la carrera</strong>: opcional; vacío, se toma de ROUVY;</li>
              <li><strong>Fecha y hora de salida*</strong>: en tu zona horaria.</li>
            </ul>
          </li>
          <li><strong>«Añadir carrera»</strong> añade una fila y <strong>«Quitar»</strong> la borra. Las filas vacías no se guardan.</li>
          <li>Pulsa <strong>«Crear»</strong>. Si alguna carrera falla, el evento no se crea y el error indica el número de fila.</li>
        </ol>
        <Shot name="manual-create" alt="Carreras del evento al crearlo" caption="La lista de carreras al crear el evento" />
        <p>También puedes añadir carreras más tarde: la edición del evento tiene un formulario para una carrera <Num>1</Num>. La carrera añadida aparece enseguida en la lista <Num>2</Num>.</p>
        <Shot name="edit-manual" alt="Añadir una carrera en la edición" />
      </section>

      <section id="open">
        <h2>Variante C. Torneo abierto</h2>
        <p>
          Un torneo abierto no tiene horario. Cada participante crea en ROUVY una carrera (Race) en un recorrido del torneo y la corre cuando quiere.
          La salida cuenta por recorrido y tipo de carrera, y en la clasificación entra el mejor tiempo de todos los intentos.
        </p>
        <ol className="guide-steps">
          <li>En «Tipo de evento» elige <strong>«Torneo abierto»</strong>.</li>
          <li>
            Fija el <strong>periodo del torneo</strong>: «El torneo empieza» y «El torneo termina». Las salidas fuera de él no cuentan.
            Ambos son opcionales: sin límites el torneo no termina. El final debe ser posterior al inicio.
          </li>
          <li>Completa el resto de pasos y pulsa <strong>«Crear»</strong>.</li>
          <li>Abre la edición del evento y añade los recorridos (ver abajo).</li>
        </ol>
        <Note kind="warning">Los recorridos del torneo abierto solo se añaden después de crear el evento, en la edición.</Note>
        <Shot name="edit-open" alt="Recorridos del torneo abierto" />
        <ol>
          <li><Num>1</Num> Pega el enlace de la página de un recorrido de ROUVY: <code>https://riders.rouvy.com/route/…</code>.</li>
          <li><Num>2</Num> Pulsa <strong>«Añadir un recorrido»</strong>.</li>
          <li><Num>3</Num> El recorrido pasa a formar parte del torneo. «Eliminar» lo quita.</li>
        </ol>
      </section>

      <section id="official">
        <h2>Variante D. Evento oficial</h2>
        <p>
          El estado <strong>«Oficial»</strong> es para eventos oficiales de ROUVY. Sus carreras las gestiona ROUVY, por eso el formulario es más corto:
          no hay casilla de carreras a mano, plantilla de búsqueda, coorganizadores ni wild cards.
        </p>
        <Shot name="official" alt="Formulario de un evento oficial" caption="Estado «Oficial»: los bloques sobrantes están ocultos" />
      </section>

      <section id="after">
        <h2>Después de crear: edición y coorganizadores</h2>
        <ul>
          <li>La edición se abre con el icono del lápiz en la fila del evento de la página «Eventos». Todos los ajustes los cambian el autor del evento y los administradores.</li>
          <li>La forma de añadir carreras (la casilla «Carreras creadas por otros usuarios de ROUVY») no se puede cambiar en la edición.</li>
          <li>Los coorganizadores ven una página simplificada: pueden añadir carreras (lanzar la sincronización o introducirlas a mano) y eliminar las que añadieron. El resto de ajustes no está disponible para ellos.</li>
        </ul>
        <Shot name="co-organizer-view" alt="Página del coorganizador" caption="Así ve un coorganizador el evento: solo puede eliminar su propia carrera" />
      </section>

      <section id="problems">
        <h2>Errores frecuentes</h2>
        <div className="guide-table-wrap">
          <table className="guide-table">
            <thead>
              <tr>
                <th>Mensaje o situación</th>
                <th>Causa y solución</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>«El nombre del evento es obligatorio»</td>
                <td>Rellena «Nombre del evento».</td>
              </tr>
              <tr>
                <td>«Los eventos de salida en grupo y de carrera solo pueden tener un recorrido»</td>
                <td>El evento tiene carreras en recorridos distintos. Elige el tipo Torneo o quita las carreras que sobran.</td>
              </tr>
              <tr>
                <td>«El torneo debe terminar después de empezar»</td>
                <td>Corrige el periodo del torneo abierto.</td>
              </tr>
              <tr>
                <td>«Indica el recorrido, el enlace a la carrera de ROUVY y la fecha/hora de salida en cada carrera»</td>
                <td>A una fila de carreras a mano le falta un campo obligatorio. Rellénalo o quita la fila.</td>
              </tr>
              <tr>
                <td>El torneo abierto se rechaza al guardar</td>
                <td>La cuenta no tiene acceso a torneos abiertos. Elige otro tipo.</td>
              </tr>
              <tr>
                <td>No hay carreras tras la sincronización</td>
                <td>
                  Comprueba que los nombres de las carreras de ROUVY contienen la plantilla, que las creaste tú o los coorganizadores
                  y que tienes ROUVY conectado. Espera unos minutos y pulsa «Actualizar la lista».
                  Si las carreras las creó otra persona, necesitas la <a href="#manual">variante B</a>: crea el evento de nuevo.
                </td>
              </tr>
              <tr>
                <td>No aparece el botón «Crear un evento»</td>
                <td>La cuenta no puede crear eventos: contacta con un administrador.</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
};

export default GuideContent;
